import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, BrowseDashboardItem } from '../api/client';
import { SkeletonCard, CardPreview, ViewToggle, useViewMode } from '../components/Layout';
import { TagSelect } from '../components/TagSelect';
export default function BrowsePage() {
  const [q, setQ] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [uploadType, setUploadType] = useState<'prototype' | 'dashboard' | ''>('');
  const [viewMode, toggleViewMode] = useViewMode();
  const [sort, setSort] = useState<'updated' | 'title' | 'rating'>('updated');
  const [items, setItems] = useState<BrowseDashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const reqRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const search = async () => {
    const reqId = ++reqRef.current;
    setLoading(true);
    setError('');
    try {
      const { items: results } = await api.browseDashboards({
        q: q || undefined,
        tags: tags.length ? tags : undefined,
        uploadType: uploadType || undefined,
        sort,
      });
      if (reqId === reqRef.current) setItems(results);
    } catch (e: unknown) {
      if (reqId === reqRef.current) setError((e as Error).message);
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    api.listTags().then((r) => setAvailableTags(r.tags ?? [])).catch(() => {});
  }, []);

  // Live search: debounce the text input; filter changes apply quickly too.
  useEffect(() => {
    debounceRef.current = setTimeout(search, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, tags, uploadType, sort]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearTimeout(debounceRef.current); // cancel the queued debounce so we search once
    search();
  };

  const gridClass = viewMode === 'list' ? 'canvas-list' : 'grid';

  return (
    <div>
      <div className="page-header">
        <h1>Browse</h1>
        <ViewToggle mode={viewMode} onToggle={toggleViewMode} />
      </div>
      <form className="browse-toolbar" onSubmit={onSubmit}>
        <input
          placeholder="Search by title…"
          aria-label="Search by title"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <TagSelect
          options={availableTags}
          value={tags}
          onChange={setTags}
          placeholder="Filter by tags…"
          showChips={false}
        />
        <select aria-label="Filter by type" value={uploadType} onChange={(e) => { setUploadType(e.target.value as 'prototype' | 'dashboard' | ''); }}>
          <option value="">All types</option>
          <option value="prototype">Prototype</option>
          <option value="dashboard">Dashboard</option>
        </select>
        <select aria-label="Sort by" value={sort} onChange={(e) => setSort(e.target.value as 'updated' | 'title' | 'rating')}>
          <option value="updated">Recently updated</option>
          <option value="title">Title A–Z</option>
          <option value="rating">Highest rated</option>
        </select>
        <button type="submit" className="btn primary" disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {tags.length > 0 && (
        <div className="browse-active-tags">
          {tags.map((t) => (
            <span key={t} className="chip">
              {t}
              <button
                type="button"
                className="chip-remove"
                aria-label={`Remove ${t}`}
                onClick={() => setTags(tags.filter((x) => x !== t))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {error && <p className="error">{error}</p>}

      <div className={gridClass}>
        {loading
          ? Array.from({ length: 6 }, (_, i) => <SkeletonCard key={i} viewMode={viewMode} />)
          : items.map((item) => {
              const overlay = item.game && item.game !== '_none'
                ? <span className="badge">{item.game}</span>
                : undefined;
              const ratingDisplay = item.ratingAverage != null
                ? <span className="browse-rating">{'★'.repeat(Math.round(item.ratingAverage))}{'☆'.repeat(5 - Math.round(item.ratingAverage))} {item.ratingAverage.toFixed(1)}</span>
                : null;
              const galleryInfo = (
                <>
                  <span className="card-preview-info-title">{item.title}</span>
                  {item.createdByName && <span className="card-preview-info-creator muted">{item.createdByName}</span>}
                  {(item.tags ?? []).length > 0 && (
                    <div className="tag-chips">
                      {(item.tags ?? []).map((t) => <span key={t} className="chip">{t}</span>)}
                    </div>
                  )}
                  {ratingDisplay}
                  <span className="card-preview-info-date">Added {new Date(item.updatedAt).toLocaleDateString()}</span>
                </>
              );
              return (
                <Link
                  key={item.dashboardId}
                  to={`/dashboard/${item.dashboardId}`}
                  className={`card dashboard-card${viewMode === 'list' ? ' dashboard-card-list' : ''}`}
                >
                  <CardPreview
                    seed={item.dashboardId}
                    title={item.title}
                    screenshotUrl={item.screenshotUrl}
                    overlay={overlay}
                    info={viewMode === 'gallery' ? galleryInfo : undefined}
                  />
                  {viewMode === 'list' && (
                    <div className="card-body">
                      <h3>{item.title}</h3>
                      {item.createdByName && <p className="muted card-creator">{item.createdByName}</p>}
                      {item.description && <p className="muted">{item.description}</p>}
                      {(item.tags ?? []).length > 0 && (
                        <div className="tag-chips">
                          {(item.tags ?? []).map((t) => <span key={t} className="chip">{t}</span>)}
                        </div>
                      )}
                      {ratingDisplay}
                      <p className="muted card-date">Added {new Date(item.updatedAt).toLocaleDateString()}</p>
                    </div>
                  )}
                </Link>
              );
            })}
        {!loading && !items.length && !error && (
          <p className="muted">
            {q || tags.length || uploadType
              ? 'No uploads match your search.'
              : 'No discoverable uploads yet.'}
          </p>
        )}
      </div>
    </div>
  );
}
